import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import { X, Search, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAllDevices, useUserDevices } from '@/hooks/useUsers';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import type { User } from '@/lib/types';

interface Props {
  user: User | null;            // null = closed
  onClose: () => void;
}

export function AssignDevicesModal({ user, onClose }: Props) {
  const open = user !== null;
  const allDevicesQ = useAllDevices(open);
  const userDevicesQ = useUserDevices(user?.id ?? null);
  const toast = useToast();

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');

  // Seed selected set whenever the user changes / data lands
  useEffect(() => {
    if (open && userDevicesQ.data) {
      setSelected(new Set(userDevicesQ.data.map((d) => d.id)));
    }
  }, [open, userDevicesQ.data]);

  // Reset query whenever modal opens
  useEffect(() => { if (open) setQuery(''); }, [open]);

  const filtered = useMemo(() => {
    const list = allDevicesQ.data ?? [];
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter((d) =>
      d.client_name.toLowerCase().includes(q) ||
      d.client_id.toLowerCase().includes(q),
    );
  }, [allDevicesQ.data, query]);

  const toggle = (id: number) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const allShownSelected = filtered.length > 0 && filtered.every((d) => selected.has(d.id));
  const toggleAllShown = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (allShownSelected) filtered.forEach((d) => n.delete(d.id));
      else filtered.forEach((d) => n.add(d.id));
      return n;
    });
  };

  const save = useMutation({
    mutationFn: () => api(`/api/auth/users/${user!.id}/devices`, {
      method: 'POST',
      body: { deviceIds: Array.from(selected) },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-devices', user?.id] });
      toast.ok('Device assignments saved');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to save assignments'),
  });

  const loading = allDevicesQ.isLoading || userDevicesQ.isLoading;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 bg-black/40"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
              >
                <div className="w-full max-w-md max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                  <header className="flex items-center justify-between px-5 h-14 border-b border-slate-200 shrink-0">
                    <div className="min-w-0">
                      <Dialog.Title className="font-bold text-ink">Assign devices</Dialog.Title>
                      {user && (
                        <p className="text-xs text-slate-500">to <span className="font-semibold text-ink">{user.username}</span></p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                      <X className="w-4 h-4" />
                    </Button>
                  </header>

                  <div className="px-5 py-3 border-b border-slate-100 shrink-0">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        value={query} onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search devices"
                        className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-300 bg-white text-sm focus:border-ink"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {loading && (
                      <div className="p-4 space-y-2">
                        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                      </div>
                    )}

                    {!loading && (allDevicesQ.data ?? []).length === 0 && (
                      <div className="text-center py-12 text-sm text-slate-500">
                        <Cpu className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                        No devices in the system yet.
                      </div>
                    )}

                    {!loading && (allDevicesQ.data ?? []).length > 0 && (
                      <ul className="divide-y divide-slate-100">
                        {filtered.length > 0 && (
                          <li>
                            <button
                              onClick={toggleAllShown}
                              className="w-full px-5 py-2 flex items-center gap-3 text-xs font-semibold text-slate-500 hover:bg-slate-50 uppercase tracking-wider"
                            >
                              <input type="checkbox" readOnly checked={allShownSelected} className="w-4 h-4 accent-ink" />
                              {allShownSelected ? 'Deselect' : 'Select'} all shown ({filtered.length})
                            </button>
                          </li>
                        )}
                        {filtered.map((d) => {
                          const checked = selected.has(d.id);
                          return (
                            <li key={d.id}>
                              <label className={cn(
                                'flex items-center gap-3 px-5 py-2.5 cursor-pointer transition-colors',
                                checked ? 'bg-amber-50' : 'hover:bg-slate-50',
                              )}>
                                <input
                                  type="checkbox" checked={checked}
                                  onChange={() => toggle(d.id)}
                                  className="w-4 h-4 accent-ink"
                                />
                                <span className="flex-1 min-w-0">
                                  <span className="block text-sm font-semibold text-ink truncate">{d.client_name}</span>
                                  <span className="block text-xs text-slate-500 font-mono truncate">{d.client_id}</span>
                                </span>
                                {!!d.online && <span className="text-[10px] font-bold text-emerald-600 uppercase">Online</span>}
                              </label>
                            </li>
                          );
                        })}
                        {filtered.length === 0 && query && (
                          <li className="text-center py-8 text-sm text-slate-500">No devices match "{query}"</li>
                        )}
                      </ul>
                    )}
                  </div>

                  <footer className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 shrink-0">
                    <span className="text-xs text-slate-500">
                      {selected.size} selected
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                      <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
                        Save assignments
                      </Button>
                    </div>
                  </footer>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
